/**
 * CFF Scenario Engine — Pure TypeScript, no React dependencies
 *
 * Takes a base forecast (day-by-day cashflow) and a scenario's changes array,
 * returns a projected day-by-day cashflow with derived metrics.
 *
 * Design principles (per docs/cash-forecaster-engine-spec.md):
 * - Adjustments are applied SIMULTANEOUSLY against the base (not chained)
 * - The same engine runs in demo mode and live mode — data source is irrelevant
 * - All metrics are DERIVED from the projected cashflow — nothing is hardcoded
 * - Coverage = months until cash hits zero at projected net burn rate
 * - Net Impact = scenario ending cash minus base ending cash at window end
 */

import type { ForecastDataPoint } from '../types';
import type { ScenarioChange, ScenarioMetrics } from '../types/scenarios';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface DailyDelta {
  inflow: number;   // Additional cash in on this day (positive)
  outflow: number;  // Additional cash out on this day (positive = more going out)
}

export interface ProjectedForecastPoint {
  date: Date;
  baseBalance: number;
  scenarioBalance: number;
  baseIncoming: number;
  baseOutgoing: number;
  scenarioIncoming: number;
  scenarioOutgoing: number;
  delta: number; // scenarioBalance - baseBalance
}

export interface ScenarioEngineResult {
  projectedForecast: ProjectedForecastPoint[];
  metrics: ScenarioMetrics;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAYS_IN_MONTH = 30.44; // Average days per month

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalise a date to midnight (start of day) for day-level comparison.
 */
function toMidnight(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Return the number of whole days between two dates (b - a).
 * Negative if b is before a.
 */
function daysBetween(a: Date, b: Date): number {
  return Math.round((toMidnight(b).getTime() - toMidnight(a).getTime()) / MS_PER_DAY);
}

/**
 * Clamp a value between min and max.
 */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

// ─────────────────────────────────────────────────────────────────────────────
// Change → Daily Delta mapping
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Given a single ScenarioChange, build a map of day-index → DailyDelta.
 * Day index 0 = today (same as base forecast day 0).
 *
 * The engine calls this for every change, then sums all deltas per day
 * SIMULTANEOUSLY (so changes don't affect each other — spec Q1).
 */
function buildDeltaMap(
  change: ScenarioChange,
  today: Date,
  windowDays: number
): Map<number, DailyDelta> {
  const map = new Map<number, DailyDelta>();

  const addDelta = (dayIndex: number, inflow: number, outflow: number) => {
    if (dayIndex < 0 || dayIndex > windowDays) return;
    const existing = map.get(dayIndex) ?? { inflow: 0, outflow: 0 };
    map.set(dayIndex, {
      inflow: existing.inflow + inflow,
      outflow: existing.outflow + outflow,
    });
  };

  const effectiveDay = change.effectiveDate
    ? daysBetween(today, change.effectiveDate)
    : daysBetween(today, change.date);

  const endDay = change.endDate
    ? daysBetween(today, change.endDate)
    : windowDays;

  const startDay = clamp(effectiveDay, 0, windowDays);
  const finishDay = clamp(endDay, 0, windowDays);

  switch (change.type) {

    // ── Cost changes ──────────────────────────────────────────────────────────
    case 'change-costs':
    case 'adjust-cost':
    case 'add-cost': {
      const details = change.details as Record<string, unknown>;
      const isReduction = details.changeType === 'reduction';

      // Priority: explicit monthly figures → derive from total amount over period
      const monthlySavings = (details.monthlySavings as number) ??
                             (details.monthlySaving as number) ?? 0; // Accept both spellings
      const monthlyIncrease = (details.monthlyIncrease as number) ??
                              (details.monthlyCost as number) ?? 0;
      const monthlyAmount = (details.monthlyAmount as number) ?? 0;

      // If no monthly figure provided, derive from total amount spread over window
      const periodDays = Math.max(finishDay - startDay + 1, 1);
      const derivedDailyFromAmount = change.amount !== undefined
        ? Math.abs(change.amount) / periodDays
        : 0;

      let dailyAmount: number;
      if (isReduction) {
        dailyAmount = monthlySavings > 0
          ? monthlySavings / DAYS_IN_MONTH
          : monthlyAmount > 0
            ? monthlyAmount / DAYS_IN_MONTH
            : derivedDailyFromAmount;
      } else {
        dailyAmount = monthlyIncrease > 0
          ? monthlyIncrease / DAYS_IN_MONTH
          : monthlyAmount > 0
            ? monthlyAmount / DAYS_IN_MONTH
            : derivedDailyFromAmount;
      }

      // Determine direction from amount sign when changeType not explicit
      const isNegativeAmount = change.amount !== undefined && change.amount < 0;
      const effectivelyReduction = isReduction && !isNegativeAmount;

      for (let d = startDay; d <= finishDay; d++) {
        if (effectivelyReduction || (!isReduction && !isNegativeAmount && monthlySavings > 0)) {
          // Savings — less cash going out
          addDelta(d, 0, -dailyAmount);
        } else {
          // Cost increase — more cash going out
          addDelta(d, 0, dailyAmount);
        }
      }
      break;
    }

    // ── Revenue changes ───────────────────────────────────────────────────────
    case 'add-revenue':
    case 'adjust-revenue':
    case 'change-revenue': {
      const details = change.details as Record<string, unknown>;
      const monthlyAmount = (details.monthlyAmount as number) ??
                            (details.monthlyRevenue as number) ?? 0;

      // For transaction adjustments: derive from valueChange or total amount over period
      const periodDays = Math.max(finishDay - startDay + 1, 1);
      const valueChange = (details.valueChange as number) ?? 0;
      const derivedDailyFromAmount = change.amount !== undefined
        ? Math.abs(change.amount) / periodDays
        : 0;

      const dailyAmount = monthlyAmount !== 0
        ? monthlyAmount / DAYS_IN_MONTH
        : valueChange !== 0
          ? Math.abs(valueChange) / periodDays
          : derivedDailyFromAmount;

      // Determine direction: negative monthlyAmount, negative valueChange, or negative amount = less revenue
      const isNegative = monthlyAmount < 0
        || (monthlyAmount === 0 && valueChange < 0)
        || (monthlyAmount === 0 && valueChange === 0 && change.amount !== undefined && change.amount < 0);

      for (let d = startDay; d <= finishDay; d++) {
        addDelta(d, isNegative ? -dailyAmount : dailyAmount, 0);
      }
      break;
    }

    // ── Win a customer (one-off or recurring) ─────────────────────────────────
    case 'win-customer': {
      const details = change.details as Record<string, unknown>;
      const contractValue = (details.contractValue as number) ??
                            (details.totalValue as number) ?? change.amount ?? 0;
      const monthlyValue = (details.monthlyValue as number) ?? 0;

      // One-off deposit/payment on effective date
      if (contractValue > 0 && !monthlyValue) {
        addDelta(startDay, contractValue, 0);
      }

      // Recurring monthly revenue from effective date
      if (monthlyValue > 0) {
        const dailyAmount = monthlyValue / DAYS_IN_MONTH;
        for (let d = startDay; d <= finishDay; d++) {
          addDelta(d, dailyAmount, 0);
        }
      }
      break;
    }

    // ── Lose a customer ───────────────────────────────────────────────────────
    case 'lose-customer': {
      const details = change.details as Record<string, unknown>;
      const periodDays = Math.max(finishDay - startDay + 1, 1);

      // If it's a one-off withheld/lost amount (no end date), treat as single day outflow reduction
      const isOneOff = !change.endDate;
      if (isOneOff) {
        const amount = Math.abs(change.amount ?? 0);
        addDelta(startDay, -amount, 0); // One-off cash we won't receive
        break;
      }

      const monthlyRevenueLost = (details.monthlyRevenueLost as number) ??
                                  (details.monthlyValue as number) ?? 0;
      // Derive from total amount if no monthly figure
      const derivedMonthly = change.amount !== undefined
        ? (Math.abs(change.amount) / periodDays) * DAYS_IN_MONTH
        : 0;
      const effectiveMonthly = monthlyRevenueLost > 0 ? monthlyRevenueLost : derivedMonthly;
      const dailyLoss = effectiveMonthly / DAYS_IN_MONTH;

      for (let d = startDay; d <= finishDay; d++) {
        addDelta(d, -dailyLoss, 0); // Less cash coming in
      }
      break;
    }

    // ── Capital purchase (one-off cash out) ───────────────────────────────────
    case 'capital-purchase': {
      const amount = Math.abs(change.amount ?? 0); // Always an outflow regardless of sign
      addDelta(startDay, 0, amount);
      break;
    }

    // ── Hire employees (recurring payroll cost from effective date) ───────────
    case 'hire-employees': {
      const details = change.details as Record<string, unknown>;
      const monthlyCost = (details.monthlyCost as number) ??
                          (details.monthlyPayroll as number) ?? 0;

      // If no monthly cost specified, treat as one-off lump sum on effective date
      if (monthlyCost > 0) {
        const dailyCost = monthlyCost / DAYS_IN_MONTH;
        for (let d = startDay; d <= finishDay; d++) {
          addDelta(d, 0, dailyCost);
        }
      } else if (change.amount !== undefined && change.amount !== 0) {
        // One-off or lump sum cost — spread over period or apply on start day
        const periodDays = Math.max(finishDay - startDay + 1, 1);
        const dailyCost = Math.abs(change.amount) / periodDays;
        for (let d = startDay; d <= finishDay; d++) {
          addDelta(d, 0, dailyCost);
        }
      }
      break;
    }

    // ── Delay payment (shift outflow from original date to new date) ──────────
    case 'delay-payment': {
      const originalDay = change.originalDueDate
        ? daysBetween(today, change.originalDueDate)
        : startDay;
      const newDay = change.newDueDate
        ? daysBetween(today, change.newDueDate)
        : startDay + (change.delayDays ?? 30);
      const amount = change.amount ?? 0;

      // Remove from original date (less cash goes out that day)
      addDelta(originalDay, 0, -amount);
      // Add to new date (more cash goes out that day)
      addDelta(newDay, 0, amount);
      break;
    }

    // ── Accelerate payment (bring inflow forward) ─────────────────────────────
    case 'accelerate-payment': {
      const originalDay = change.originalDueDate
        ? daysBetween(today, change.originalDueDate)
        : startDay + 30;
      const newDay = startDay;
      const amount = change.amount ?? 0;

      // Remove from original date (less cash comes in then)
      addDelta(originalDay, -amount, 0);
      // Add to new (earlier) date (more cash comes in sooner)
      addDelta(newDay, amount, 0);
      break;
    }

    // ── Custom / unknown types ────────────────────────────────────────────────
    case 'custom':
    default: {
      // Apply amount as a one-off inflow (positive) or outflow (negative)
      const amount = change.amount ?? 0;
      if (amount >= 0) {
        addDelta(startDay, amount, 0);
      } else {
        addDelta(startDay, 0, Math.abs(amount));
      }
      break;
    }
  }

  return map;
}

// ─────────────────────────────────────────────────────────────────────────────
// Derived Metrics
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derive ScenarioMetrics from a projected forecast.
 *
 * Definitions (per docs/cash-forecaster-engine-spec.md, Section 6.3):
 *
 * minCash        — Lowest daily closing balance in the window
 * minCashDate    — Date that lowest balance occurs
 * endingCash     — Closing balance on the last day
 * netImpact      — scenarioEndingCash − baseEndingCash
 * runway         — months until cash hits zero at avg net daily burn rate
 *                  (from current balance, NOT scenario ending balance)
 *                  "No burn" state = Infinity (coverage/runway is shown as such)
 *
 * revenueChange/costChange are percentage and absolute amounts of total
 * inflow/outflow changes across the full window.
 */
function deriveMetrics(
  projected: ProjectedForecastPoint[],
  startingCash: number
): ScenarioMetrics {
  if (projected.length === 0) {
    return {
      minCash: startingCash,
      minCashDate: new Date(),
      runway: 0,
      revenueChange: 0,
      revenueChangeAmount: 0,
      costChange: 0,
      costChangeAmount: 0,
      netImpact: 0,
      endingCash: startingCash,
    };
  }

  // Min cash point
  let minCash = projected[0].scenarioBalance;
  let minCashDate = projected[0].date;

  // Totals for revenue/cost change calculation
  let totalBaseInflow = 0;
  let totalScenarioInflow = 0;
  let totalBaseOutflow = 0;
  let totalScenarioOutflow = 0;

  for (const point of projected) {
    if (point.scenarioBalance < minCash) {
      minCash = point.scenarioBalance;
      minCashDate = point.date;
    }
    totalBaseInflow += point.baseIncoming;
    totalScenarioInflow += point.scenarioIncoming;
    totalBaseOutflow += point.baseOutgoing;
    totalScenarioOutflow += point.scenarioOutgoing;
  }

  const endingPoint = projected[projected.length - 1];
  const endingCash = endingPoint.scenarioBalance;
  const baseEndingCash = endingPoint.baseBalance;
  const netImpact = endingCash - baseEndingCash;

  // Revenue change
  const revenueChangeAmount = totalScenarioInflow - totalBaseInflow;
  const revenueChange = totalBaseInflow > 0
    ? (revenueChangeAmount / totalBaseInflow) * 100
    : 0;

  // Cost change
  const costChangeAmount = totalScenarioOutflow - totalBaseOutflow;
  const costChange = totalBaseOutflow > 0
    ? (costChangeAmount / totalBaseOutflow) * 100
    : 0;

  // Coverage / Runway
  // Average net daily burn across the scenario window
  const windowDays = projected.length;
  const totalNetOutflow = totalScenarioOutflow - totalScenarioInflow;
  const avgDailyBurn = totalNetOutflow / windowDays; // Positive = burning cash

  let runway: number;
  if (avgDailyBurn <= 0) {
    // "No burn" — cash is growing or flat. Per spec Q6: Infinity, shown as "No burn"
    runway = Infinity;
  } else {
    // Months until current starting balance hits zero at this burn rate
    const daysUntilZero = startingCash / avgDailyBurn;
    runway = daysUntilZero / DAYS_IN_MONTH;
  }

  return {
    minCash: Math.round(minCash),
    minCashDate,
    runway: runway > 0 && runway < 0.1 ? 0.1 : Math.round(runway * 10) / 10,
    revenueChange: Math.round(revenueChange * 10) / 10,
    revenueChangeAmount: Math.round(revenueChangeAmount),
    costChange: Math.round(costChange * 10) / 10,
    costChangeAmount: Math.round(costChangeAmount),
    netImpact: Math.round(netImpact),
    endingCash: Math.round(endingCash),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Engine Function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Apply a set of ScenarioChanges to a base forecast.
 *
 * @param baseForecast  Day-by-day base forecast (from generateForecastData or API)
 * @param changes       Scenario adjustments to apply
 * @returns             ProjectedForecastPoints + derived ScenarioMetrics
 *
 * Rules:
 * - Changes are applied SIMULTANEOUSLY (spec Q1)
 * - Deltas are summed per day across all changes
 * - Balance never goes below -999,999,999 (prevent runaway negatives)
 */
export function applyScenarioToForecast(
  baseForecast: ForecastDataPoint[],
  changes: ScenarioChange[]
): ScenarioEngineResult {
  if (baseForecast.length === 0) {
    return {
      projectedForecast: [],
      metrics: {
        minCash: 0,
        minCashDate: new Date(),
        runway: 0,
        revenueChange: 0,
        revenueChangeAmount: 0,
        costChange: 0,
        costChangeAmount: 0,
        netImpact: 0,
        endingCash: 0,
      },
    };
  }

  const today = toMidnight(baseForecast[0].date);
  const windowDays = baseForecast.length - 1;
  const startingCash = baseForecast[0].balance;

  // ── Step 1: Build combined delta map from ALL changes simultaneously ────────
  // Each change contributes independently to the delta map — not chained.
  const combinedDeltas = new Map<number, DailyDelta>();

  for (const change of changes) {
    const changeDeltas = buildDeltaMap(change, today, windowDays);
    for (const [dayIndex, delta] of changeDeltas) {
      const existing = combinedDeltas.get(dayIndex) ?? { inflow: 0, outflow: 0 };
      combinedDeltas.set(dayIndex, {
        inflow: existing.inflow + delta.inflow,
        outflow: existing.outflow + delta.outflow,
      });
    }
  }

  // ── Step 2: Apply combined deltas to base forecast day by day ───────────────
  let scenarioBalance = startingCash;

  const projectedForecast: ProjectedForecastPoint[] = baseForecast.map((basePoint, i) => {
    const delta = combinedDeltas.get(i) ?? { inflow: 0, outflow: 0 };

    const scenarioIncoming = Math.max(0, basePoint.incoming + delta.inflow);
    const scenarioOutgoing = Math.max(0, basePoint.outgoing + delta.outflow);

    if (i === 0) {
      // Day 0: no transactions, just starting balance
      scenarioBalance = startingCash;
    } else {
      scenarioBalance = scenarioBalance + scenarioIncoming - scenarioOutgoing;
      // Floor at a very negative number to prevent runaway values
      // but allow negative cash (overdraft territory) to show in charts
      scenarioBalance = Math.max(-999999999, scenarioBalance);
    }

    return {
      date: basePoint.date,
      baseBalance: basePoint.balance,
      scenarioBalance,
      baseIncoming: basePoint.incoming,
      baseOutgoing: basePoint.outgoing,
      scenarioIncoming,
      scenarioOutgoing,
      delta: scenarioBalance - basePoint.balance,
    };
  });

  // ── Step 3: Derive metrics from projected cashflow ──────────────────────────
  const metrics = deriveMetrics(projectedForecast, startingCash);

  return { projectedForecast, metrics };
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience: calculate metrics for a scenario without the full projection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shorthand: just return the derived metrics without the full day-by-day array.
 * Used by ScenarioContext to calculate metrics for all scenarios on load.
 */
export function calculateScenarioMetrics(
  baseForecast: ForecastDataPoint[],
  changes: ScenarioChange[]
): ScenarioMetrics {
  return applyScenarioToForecast(baseForecast, changes).metrics;
}

// ─────────────────────────────────────────────────────────────────────────────
// Coverage display helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a runway/coverage value for display.
 * Handles the Infinity → "No burn" case (spec Q6).
 */
export function formatCoverageValue(runway: number): {
  display: string;
  tooltip: string;
  isNoBurn: boolean;
} {
  if (!isFinite(runway) || runway === Infinity) {
    return {
      display: 'No burn',
      tooltip:
        'Cash inflows exceed outflows — your cash position is growing. No runway calculation needed.',
      isNoBurn: true,
    };
  }

  if (runway <= 0) {
    return {
      display: '0 months',
      tooltip: 'Cash is projected to be exhausted.',
      isNoBurn: false,
    };
  }

  const months = Math.round(runway * 10) / 10;
  const years = Math.floor(months / 12);
  const remainingMonths = Math.round(months % 12);

  let display: string;
  if (years > 0 && remainingMonths > 0) {
    display = `${years}y ${remainingMonths}m`;
  } else if (years > 0) {
    display = `${years} year${years !== 1 ? 's' : ''}`;
  } else {
    display = `${months} month${months !== 1 ? 's' : ''}`;
  }

  return {
    display,
    tooltip: `At the projected net burn rate, current cash will last approximately ${display}.`,
    isNoBurn: false,
  };
}
